<template>
  <div class="modal fade receipting-modal" tabindex="-1" role="dialog" aria-hidden="true">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content border-0 shadow">
        <!-- Modal Header -->
        <div class="modal-header border-0 py-3 modal-gradient-header">
          <div class="d-flex align-items-center">
            <div class="modal-icon me-3">
              <i class="bx bx-detail text-white"></i>
            </div>
            <div>
              <h5 class="modal-title mb-0 fw-semibold text-white">Application Details</h5>
              <small class="text-white opacity-75">{{ application.billNo }}</small>
            </div>
          </div>
          <div class="d-flex align-items-center gap-2">
            <span :class="getStatusClass(application.status)" class="badge rounded-pill px-3">
              {{ application.status }}
            </span>
            <button type="button" class="btn-close btn-close-white ms-2" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
        </div>

        <!-- Modal Body -->
        <div class="modal-body p-4 modal-form-scroll">
          <div class="row g-4">
            <!-- Applicant Details -->
            <div class="col-md-6">
              <div class="info-card h-100">
                <div class="info-card-header">
                  <i class="bx bx-user text-primary"></i>
                  <span>Applicant Details</span>
                </div>
                <div class="info-card-body">
                  <div class="info-row">
                    <span class="info-label">Full Name</span>
                    <span class="info-value fw-semibold">{{ application.applicant || '-' }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">Phone Number</span>
                    <span class="info-value">{{ application.phoneNumber || '-' }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">ID Number</span>
                    <span class="info-value">{{ application.idNo || '-' }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">Email</span>
                    <span class="info-value">{{ application.email || '-' }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Business Details -->
            <div class="col-md-6">
              <div class="info-card h-100">
                <div class="info-card-header">
                  <i class="bx bx-briefcase-alt-2 text-success"></i>
                  <span>Business Details</span>
                </div>
                <div class="info-card-body">
                  <div class="info-row">
                    <span class="info-label">Business Name</span>
                    <span class="info-value fw-semibold">{{ application.businessName || '-' }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">Business ID</span>
                    <span class="info-value">{{ application.businessID || '-' }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">Category</span>
                    <span class="info-value">{{ application.businessCategory || '-' }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">Sub Category</span>
                    <span class="info-value small">{{ application.businessSubCategory || '-' }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Location Details -->
            <div class="col-md-6">
              <div class="info-card h-100">
                <div class="info-card-header">
                  <i class="bx bx-map text-info"></i>
                  <span>Location Details</span>
                </div>
                <div class="info-card-body">
                  <div class="info-row">
                    <span class="info-label">Sub County</span>
                    <span class="info-value">{{ application.subCountyName || '-' }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">Ward</span>
                    <span class="info-value">{{ application.wardName || '-' }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">Plot Number</span>
                    <span class="info-value">{{ application.plotNumber || '-' }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">Physical Address</span>
                    <span class="info-value">{{ application.physicalAddress || '-' }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Application Info -->
            <div class="col-md-6">
              <div class="info-card h-100">
                <div class="info-card-header">
                  <i class="bx bx-calendar text-warning"></i>
                  <span>Application Info</span>
                </div>
                <div class="info-card-body">
                  <div class="info-row">
                    <span class="info-label">Fiscal Year</span>
                    <span class="info-value">{{ application.fiscalYear || '-' }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">Date Applied</span>
                    <span class="info-value">{{ formatDate(application.dateOfIssue) }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">Current Status</span>
                    <span class="info-value">
                      <span :class="getStatusClass(application.status)" class="badge rounded-pill px-2">
                        {{ application.status }}
                      </span>
                    </span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">Processing Stage</span>
                    <span class="info-value">{{ application.stageStatus || 'Ongoing' }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Invoice Details -->
            <div class="col-12">
              <div class="info-card">
                <div class="info-card-header">
                  <i class="bx bx-receipt text-danger"></i>
                  <span>Invoice Details</span>
                </div>
                <div class="info-card-body">
                  <div class="row mb-3">
                    <div class="col-md-4">
                      <div class="info-row">
                        <span class="info-label">Invoice No.</span>
                        <span class="info-value text-primary fw-semibold">{{ application.billNo || '-' }}</span>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="info-row">
                        <span class="info-label">Date Invoiced</span>
                        <span class="info-value">{{ formatDate(application.dateOfIssue) }}</span>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="info-row">
                        <span class="info-label">Issuing Officer</span>
                        <span class="info-value">{{ application.issuingOfficer || '-' }}</span>
                      </div>
                    </div>
                  </div>
                  
                  <hr class="my-3">
                  
                  <!-- Amount Summary -->
                  <div class="row g-3">
                    <div class="col-md-4">
                      <div class="amount-box bg-light">
                        <span class="amount-label">Invoice Total</span>
                        <span class="amount-value text-dark">{{ formatCurrency(application.billTotal) }}</span>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="amount-box bg-success-subtle">
                        <span class="amount-label">Amount Paid</span>
                        <span class="amount-value text-success">{{ formatCurrency(application.receiptAmount) }}</span>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="amount-box" :class="parseFloat(application.billBalance) > 0 ? 'bg-danger-subtle' : 'bg-success-subtle'">
                        <span class="amount-label">Balance Due</span>
                        <span class="amount-value" :class="parseFloat(application.billBalance) > 0 ? 'text-danger' : 'text-success'">
                          {{ formatCurrency(application.billBalance) }}
                        </span>
                      </div>
                    </div>
                  </div>

                  <!-- Payment Status Bar -->
                  <div class="mt-3">
                    <div class="d-flex justify-content-between align-items-center mb-1">
                      <small class="text-muted">Payment Progress</small>
                      <small class="fw-semibold">{{ getPaymentPercentage }}%</small>
                    </div>
                    <div class="progress" style="height: 8px;">
                      <div class="progress-bar bg-success" 
                           role="progressbar" 
                           :style="{ width: getPaymentPercentage + '%' }">
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Comments Section (if any) -->
            <div class="col-12" v-if="application.comments">
              <div class="info-card">
                <div class="info-card-header">
                  <i class="bx bx-comment-detail text-secondary"></i>
                  <span>Comments</span>
                </div>
                <div class="info-card-body">
                  <p class="mb-0 text-muted">{{ application.comments }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="modal-footer bg-light border-0 py-3">
          <button type="button" class="btn btn-light" data-bs-dismiss="modal">
            <i class="bx bx-x me-1"></i> Close
          </button>
          <button @click="printBill()" type="button" class="btn btn-primary">
            <i class="bx bx-printer me-1"></i> Print Invoice
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";

export default {
  name: "OngoingDetailsModal",
  props: {
    application: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    getPaymentPercentage() {
      const total = parseFloat(this.application.billTotal) || 0;
      const paid = parseFloat(this.application.receiptAmount) || 0;
      if (total === 0) return 0;
      return Math.min(Math.round((paid / total) * 100), 100);
    }
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return '-';
      return moment(dateString).format('DD MMM YYYY');
    },

    formatCurrency(amount) {
      if (!amount || isNaN(amount)) return 'KES 0';
      return 'KES ' + parseFloat(amount).toLocaleString('en-KE');
    },

    getStatusClass(status) {
      const map = {
        'PAID': 'bg-success',
        'PENDING': 'bg-warning text-dark',
        'APPROVED': 'bg-info',
        'ONGOING': 'bg-primary',
        'PROCESSING': 'bg-info'
      };
      return map[status?.toUpperCase()] || 'bg-secondary';
    },

    printBill() {
      this.$emit('print-bill', this.application.billNo);
    }
  }
}
</script>

<style scoped>
/* Modal Header Gradient */
.modal-gradient-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.modal-icon {
  width: 45px;
  height: 45px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
}

/* Modal Scroll */
.modal-form-scroll {
  max-height: calc(100vh - 220px);
  overflow-y: auto;
}

.modal-form-scroll::-webkit-scrollbar {
  width: 6px;
}

.modal-form-scroll::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.modal-form-scroll::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.modal-form-scroll::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}

/* Info Card */
.info-card {
  background: #f8f9fa;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #e9ecef;
}

.info-card-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: white;
  font-weight: 600;
  font-size: 0.9rem;
  color: #495057;
  border-bottom: 2px solid #e9ecef;
}

.info-card-header i {
  font-size: 1.25rem;
}

.info-card-body {
  padding: 1rem;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px dashed #e9ecef;
}

.info-row:last-child {
  border-bottom: none;
}

.info-label {
  color: #6c757d;
  font-size: 0.8rem;
}

.info-value {
  font-size: 0.875rem;
  text-align: right;
  max-width: 60%;
  word-break: break-word;
}

/* Amount Box */
.amount-box {
  padding: 1rem;
  border-radius: 8px;
  text-align: center;
}

.amount-label {
  display: block;
  font-size: 0.75rem;
  color: #6c757d;
  margin-bottom: 0.25rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.amount-value {
  display: block;
  font-size: 1.25rem;
  font-weight: 700;
}

/* Background Subtle Colors */
.bg-success-subtle {
  background-color: rgba(25, 135, 84, 0.1);
}

.bg-danger-subtle {
  background-color: rgba(220, 53, 69, 0.1);
}

/* Progress Bar */
.progress {
  border-radius: 10px;
  background-color: #e9ecef;
}

.progress-bar {
  border-radius: 10px;
}
</style>
